import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import BuildIcon from "@mui/icons-material/Build";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { getCspmResources, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";
import ErrorUI from "./ErrorUI";
import RemediationSnackbarProvider from "../../RemediationHub/components/common/SnackbarHost";
import RequestFixForm from "../../RemediationHub/components/RequestFixForm";
import { fetchActiveRequests } from "../../../store/remediation/remediationSlice";
import ActiveRemediationHint, {
  ActiveRemediationDetailPanel,
  RemediationStatusLegend,
} from "./ActiveRemediationHint";
import { CloudProviderContext } from "../../../CloudProviderContext";
import RemediationPreviewPanel, { isHttpUrlString } from "../../../components/Common/RemediationPreviewPanel";
import {
  ACCOUNT_DETAIL_FONT_FAMILY,
  ACCOUNT_DETAIL_FONT_SX,
} from "../../../components/AccountDetailPolicy/accountDetailTypography";

const ACCENT = "#F98125";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const SUMMARY_CHIP_SX = {
  height: 24,
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#475569",
  "& .MuiChip-label": { px: 1 },
};

const SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 240 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const SELECT_SX = {
  height: 40,
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
};

const MENU_ITEM_SX = {
  fontSize: "0.875rem",
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
};

const statusChipSx = (status) => {
  const value = (status || "").toUpperCase();
  if (value === "FAILED") {
    return { bgcolor: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" };
  }
  if (value === "PASSED") {
    return { bgcolor: "#ecfdf5", color: "#10b981", border: "1px solid #a7f3d0" };
  }
  if (value === "MANUAL") {
    return { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };
  }
  return { bgcolor: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" };
};

function getSeverityBadgeProps(sev) {
  const s = (sev || "").toUpperCase();
  let text = "#334155";
  if (s === "CRITICAL") text = "#c62828";
  else if (s === "HIGH") text = "#ef6c00";
  else if (s === "MEDIUM") text = "#ca8a04";
  else if (s === "LOW") text = "#388e3c";
  else if (s === "INFORMATIONAL") text = "#1976d2";
  return { text };
}

const CSPMResourcesDrawer = ({
  open,
  onClose,
  checkId,
  runId,
  availableRegions = [],
  availableTypes = [],
  checkTitle = "",
  accountNumber,
  isRemediable = false,
}) => {
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  const { isGettingCspmResources, getCspmResourcesError, getCspmResourcesMessage, cspmResources } = useSelector((state) => state.cspm);

  const activeRequestsKey =
    accountNumber && checkId ? `${accountNumber}:${checkId}` : null;
  const activeRequestsEntry = useSelector((state) =>
    activeRequestsKey ? state.remediation?.activeRequestsByKey?.[activeRequestsKey] : null
  );

  const activeRequestByArn = useMemo(() => {
    const map = new Map();
    const rows = activeRequestsEntry?.active_requests;
    if (!Array.isArray(rows)) return map;
    for (const item of rows) {
      if (item?.resource_arn && !map.has(item.resource_arn)) {
        map.set(item.resource_arn, item);
      }
    }
    return map;
  }, [activeRequestsEntry]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedArns, setSelectedArns] = useState(() => new Set());
  const [fixFormOpen, setFixFormOpen] = useState(false);
  const [remediationPreviewUrl, setRemediationPreviewUrl] = useState(null);

  const openRemediationPreview = useCallback((url) => {
    if (isHttpUrlString(url)) setRemediationPreviewUrl(String(url).trim());
    else if (url) window.open(String(url), "_blank", "noopener,noreferrer");
  }, []);

  const closeRemediationPreview = useCallback(() => {
    setRemediationPreviewUrl(null);
  }, []);

  useEffect(() => {
    if (!open) setRemediationPreviewUrl(null);
  }, [open]);

  useEffect(() => {
    if (open && checkId && runId) {
      setLoading(true);
      dispatch(clearAllCspmMessages());
      dispatch(getCspmResources({ checkId, runId }));
      setSelectedRegion('all');
      setSelectedService('all');
      setStatusFilter('FAILED');
      setSearch('');
      setExpandedRow(null);
      setSelectedArns(new Set());
    }
  }, [open, checkId, runId, dispatch]);

  useEffect(() => {
    if (open && accountNumber && checkId && isRemediable) {
      dispatch(fetchActiveRequests({ account_number: accountNumber, check_id: checkId }));
    }
  }, [open, accountNumber, checkId, isRemediable, dispatch]);

  useEffect(() => {
    const handleFilterEvent = (event) => {
      setStatusFilter(event.detail.filter);
    };
    window.addEventListener('setCspmFilter', handleFilterEvent);
    return () => window.removeEventListener('setCspmFilter', handleFilterEvent);
  }, []);

  useEffect(() => {
    if (!open) {
      setStatusFilter(null);
      setSelectedRegion('all');
      setSelectedService('all');
      setSearch('');
      setExpandedRow(null);
      setSelectedArns(new Set());
      setFixFormOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (getCspmResourcesMessage || getCspmResourcesError) {
      setLoading(false);
      dispatch(clearAllCspmMessages());
    }
  }, [getCspmResourcesMessage, getCspmResourcesError, dispatch]);

  const filteredResources = useMemo(() => {
    if (!cspmResources?.auditCheckList) return [];
    let resources = [...cspmResources.auditCheckList];
    if (selectedRegion !== 'all') {
      resources = resources.filter(r => r.region === selectedRegion);
    }
    if (selectedService !== 'all') {
      resources = resources.filter(r => r.Service === selectedService);
    }
    if (statusFilter) {
      resources = resources.filter(r => r.status === statusFilter);
    }
    if (search) {
      const searchLower = search.toLowerCase();
      resources = resources.filter(resource =>
        Object.values(resource).some(val =>
          val && String(val).toLowerCase().includes(searchLower)
        )
      );
    }
    const severityOrder = { CRITICAL: 1, HIGH: 2, MEDIUM: 3, LOW: 4, INFORMATIONAL: 5 };
    resources.sort((a, b) => {
      if ((a.status === 'FAILED') && (b.status !== 'FAILED')) return -1;
      if ((a.status !== 'FAILED') && (b.status === 'FAILED')) return 1;
      const sevA = severityOrder[(a.Severity || '').toUpperCase()] || 99;
      const sevB = severityOrder[(b.Severity || '').toUpperCase()] || 99;
      return sevA - sevB;
    });
    return resources;
  }, [cspmResources, search, selectedRegion, selectedService, statusFilter]);

  const failedFiltered = useMemo(
    () => filteredResources.filter((r) => r.status === 'FAILED' && r.resource_arn),
    [filteredResources]
  );

  const selectableFailed = useMemo(
    () => failedFiltered.filter((r) => !activeRequestByArn.has(r.resource_arn)),
    [failedFiltered, activeRequestByArn]
  );

  const hasFailedResources = useMemo(
    () =>
      (cspmResources?.failedResources ?? 0) > 0 ||
      (cspmResources?.auditCheckList?.some((r) => r.status === 'FAILED') ?? false),
    [cspmResources]
  );

  const showRequestFix =
    isRemediable && statusFilter !== 'PASSED' && hasFailedResources;

  useEffect(() => {
    if (!showRequestFix) {
      setSelectedArns(new Set());
      setFixFormOpen(false);
    }
  }, [showRequestFix]);

  useEffect(() => {
    if (activeRequestByArn.size === 0) return;
    setSelectedArns((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const arn of prev) {
        if (activeRequestByArn.has(arn)) {
          next.delete(arn);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [activeRequestByArn]);

  const toggleArn = (arn) => {
    setSelectedArns((prev) => {
      const next = new Set(prev);
      if (next.has(arn)) next.delete(arn);
      else next.add(arn);
      return next;
    });
  };

  const selectAllFailedVisible = () => {
    setSelectedArns(new Set(selectableFailed.map((r) => r.resource_arn)));
  };

  const clearSelection = () => setSelectedArns(new Set());

  const allFailedVisibleSelected =
    selectableFailed.length > 0 &&
    selectableFailed.every((r) => selectedArns.has(r.resource_arn));

  const refetchActiveRequests = () => {
    if (accountNumber && checkId) {
      dispatch(fetchActiveRequests({ account_number: accountNumber, check_id: checkId }));
    }
  };

  return (
  <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520, md: 720, lg: 880, xl: 1040 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#fff",
          borderLeft: "1px solid #e2e8f0",
          boxShadow: "none",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#111827",
                letterSpacing: "-0.01em",
              }}
            >
              Resource list
            </Typography>
            <Typography
              sx={{
                color: "#64748b",
                mt: 0.5,
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {checkTitle || `Check ${checkId}`}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close resource list"
            sx={{
              color: "#94a3b8",
              borderRadius: "8px",
              "&:hover": { color: "#64748b", backgroundColor: "#f1f5f9" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading || isGettingCspmResources ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        ) : getCspmResourcesError ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <ErrorUI
              error={getCspmResourcesError}
              onRetry={() => {
                setLoading(true);
                dispatch(getCspmResources({ checkId, runId }));
              }}
            />
          </Box>
        ) : !cspmResources || !cspmResources.auditCheckList || cspmResources.auditCheckList.length === 0 ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem", fontWeight: 500 }}>
              No resources data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
                p: 1.5,
                borderRadius: "12px",
                backgroundColor: "#f3f4f6",
                border: "1px solid #e2e8f0",
                flexShrink: 0,
                alignItems: "center",
              }}
            >
              <Chip
                size="small"
                label={`Total ${cspmResources.totalResources || filteredResources.length}`}
                sx={SUMMARY_CHIP_SX}
              />
              {showRequestFix && (
                <Chip
                  size="small"
                  label={
                    activeRequestsEntry?.status === "loading"
                      ? "Active Fixes …"
                      : `Active Fixes ${
                          activeRequestsEntry?.status === "succeeded" ? activeRequestByArn.size : 0
                        }`
                  }
                  sx={{
                    ...SUMMARY_CHIP_SX,
                    ...(activeRequestByArn.size > 0
                      ? { bgcolor: "#e3f2fd", color: "#1565c0", borderColor: "#90caf9" }
                      : {}),
                  }}
                />
              )}
              <Chip
                size="small"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>Passed {cspmResources.passedResources || 0}</span>
                    {statusFilter === "PASSED" ? (
                      <RemoveIcon sx={{ fontSize: 14, color: "#166534" }} />
                    ) : (
                      <AddIcon sx={{ fontSize: 14, color: "#166534" }} />
                    )}
                  </Box>
                }
                onClick={() => setStatusFilter(statusFilter === "PASSED" ? null : "PASSED")}
                sx={{
                  ...SUMMARY_CHIP_SX,
                  cursor: "pointer",
                  bgcolor: statusFilter === "PASSED" ? "#bbf7d0" : "#dcfce7",
                  color: "#166534",
                  borderColor: statusFilter === "PASSED" ? "#86efac" : "#bbf7d0",
                }}
              />
              <Chip
                size="small"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>Failed {cspmResources.failedResources || 0}</span>
                    {statusFilter === "FAILED" ? (
                      <RemoveIcon sx={{ fontSize: 14, color: "#991b1b" }} />
                    ) : (
                      <AddIcon sx={{ fontSize: 14, color: "#991b1b" }} />
                    )}
                  </Box>
                }
                onClick={() => setStatusFilter(statusFilter === "FAILED" ? null : "FAILED")}
                sx={{
                  ...SUMMARY_CHIP_SX,
                  cursor: "pointer",
                  bgcolor: statusFilter === "FAILED" ? "#fecaca" : "#fee2e2",
                  color: "#991b1b",
                  borderColor: statusFilter === "FAILED" ? "#f87171" : "#fecaca",
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                mb: 1.5,
                flexShrink: 0,
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                    "&.Mui-focused": { color: "#111827" },
                  }}
                >
                  Region
                </InputLabel>
                <Select
                  value={selectedRegion}
                  label="Region"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  sx={SELECT_SX}
                >
                  <MenuItem value="all" sx={MENU_ITEM_SX}>
                    All
                  </MenuItem>
                  {availableRegions.map((region) => (
                    <MenuItem key={region} value={region} sx={MENU_ITEM_SX}>
                      {region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                    "&.Mui-focused": { color: "#111827" },
                  }}
                >
                  Service
                </InputLabel>
                <Select
                  value={selectedService}
                  label="Service"
                  onChange={(e) => setSelectedService(e.target.value)}
                  sx={SELECT_SX}
                >
                  <MenuItem value="all" sx={MENU_ITEM_SX}>
                    All
                  </MenuItem>
                  {availableTypes.map((service) => (
                    <MenuItem key={service} value={service} sx={MENU_ITEM_SX}>
                      {service}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flexGrow: 1, minWidth: 8 }} />
              <TextField
                size="small"
                placeholder="Search resources"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={SEARCH_FIELD_SX}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {showRequestFix && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  flexShrink: 0,
                  border: "1px solid #bbf7d0",
                  backgroundColor: "#f0fdf4",
                  borderRadius: "12px",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#166534" }}>
                    {selectedArns.size > 0
                      ? `${selectedArns.size} failed resource(s) selected`
                      : "Select failed resources to file a task"}
                  </Typography>
                  <Button
                    size="small"
                    onClick={selectAllFailedVisible}
                    disabled={selectableFailed.length === 0}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#1976d2",
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    Select all failed
                  </Button>
                  {selectedArns.size > 0 && (
                    <Button
                      size="small"
                      onClick={clearSelection}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#64748b",
                        minWidth: 0,
                        px: 1,
                      }}
                    >
                      Clear
                    </Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<BuildIcon />}
                    disabled={selectedArns.size === 0}
                    onClick={() => setFixFormOpen(true)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "8px",
                      boxShadow: "none",
                      backgroundColor: "#2e7d32",
                      "&:hover": { backgroundColor: "#1b5e20", boxShadow: "none" },
                      "&.Mui-disabled": { backgroundColor: "#a7f3d0", color: "#fff" },
                    }}
                  >
                    File task ({selectedArns.size || 0})
                  </Button>
                </Stack>
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {showRequestFix && (
                        <TableCell padding="checkbox" sx={{ ...HEADER_CELL_SX, width: 48, px: 1 }}>
                          <Checkbox
                            size="small"
                            indeterminate={selectedArns.size > 0 && !allFailedVisibleSelected}
                            checked={allFailedVisibleSelected}
                            disabled={selectableFailed.length === 0}
                            onChange={() =>
                              allFailedVisibleSelected ? clearSelection() : selectAllFailedVisible()
                            }
                            sx={{ color: "#94a3b8", "&.Mui-checked": { color: ACCENT } }}
                          />
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ ...HEADER_CELL_SX, width: 48, px: 0.5 }} />
                      <TableCell align="left" sx={HEADER_CELL_SX}>
                        {selectedProvider === "AZURE" ? "Azure Resource ID" : "Resource ARN"}
                      </TableCell>
                      <TableCell align="center" sx={HEADER_CELL_SX}>
                        Region
                      </TableCell>
                      <TableCell align="center" sx={HEADER_CELL_SX}>
                        Service
                      </TableCell>
                      <TableCell align="center" sx={HEADER_CELL_SX}>
                        Status
                      </TableCell>
                      {showRequestFix && (
                        <TableCell align="center" sx={{ ...HEADER_CELL_SX, width: 48, px: 0.5 }} />
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredResources.map((resource, idx) => {
                      const isFailed = resource.status === "FAILED";
                      const arn = resource.resource_arn;
                      const activeReq = arn ? activeRequestByArn.get(arn) : null;
                      return (
                        <React.Fragment key={arn || idx}>
                          <TableRow
                            hover
                            selected={expandedRow === idx}
                            onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#f8fafc" },
                              "&.Mui-selected": { backgroundColor: "#f8fafc" },
                              "&.Mui-selected:hover": { backgroundColor: "#f1f5f9" },
                              ...(activeReq ? { bgcolor: "rgba(25, 118, 210, 0.04)" } : {}),
                            }}
                          >
                            {showRequestFix && (
                              <TableCell
                                padding="checkbox"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ ...BODY_CELL_SX, px: 1 }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={Boolean(arn && selectedArns.has(arn))}
                                  disabled={!isFailed || !arn || Boolean(activeReq)}
                                  onChange={() => arn && toggleArn(arn)}
                                  sx={{ color: "#94a3b8", "&.Mui-checked": { color: ACCENT } }}
                                />
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ ...BODY_CELL_SX, px: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedRow(expandedRow === idx ? null : idx);
                                }}
                                sx={{ color: "#94a3b8" }}
                              >
                                {expandedRow === idx ? (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ ...BODY_CELL_SX, maxWidth: 420, wordBreak: "break-all" }}
                            >
                              {resource.resource_arn}
                            </TableCell>
                            <TableCell align="center" sx={BODY_CELL_SX}>
                              {resource.region || "—"}
                            </TableCell>
                            <TableCell align="center" sx={BODY_CELL_SX}>
                              {resource.Service || "—"}
                            </TableCell>
                            <TableCell align="center" sx={BODY_CELL_SX}>
                              <Chip
                                size="small"
                                label={String(resource.status || "—").toUpperCase()}
                                sx={{
                                  height: 22,
                                  fontSize: "0.6875rem",
                                  fontWeight: 700,
                                  borderRadius: "6px",
                                  ...statusChipSx(resource.status),
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            </TableCell>
                            {showRequestFix && (
                              <TableCell
                                align="center"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ ...BODY_CELL_SX, px: 0.5 }}
                              >
                                {activeReq ? (
                                  <ActiveRemediationHint request={activeReq} />
                                ) : activeRequestsEntry?.status === "loading" && isFailed ? (
                                  <CircularProgress size={16} sx={{ color: ACCENT }} />
                                ) : null}
                              </TableCell>
                            )}
                          </TableRow>
                          {expandedRow === idx && (
                            <TableRow>
                              <TableCell
                                colSpan={showRequestFix ? 7 : 5}
                                sx={{
                                  background: "#f8fafc",
                                  borderBottom: "1px solid #e2e8f0",
                                  p: 2,
                                }}
                              >
                                <Box>
                                  {activeReq && <ActiveRemediationDetailPanel request={activeReq} />}
                                  <Typography
                                    sx={{ color: "#64748b", mb: 1, fontSize: "0.8125rem", fontWeight: 500 }}
                                  >
                                    <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>
                                      Severity:
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        color: getSeverityBadgeProps(resource.Severity).text,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {resource.Severity}
                                    </Box>
                                    <Box component="span" sx={{ mx: 1, color: "#cbd5e1" }}>
                                      ·
                                    </Box>
                                    <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>
                                      Type:
                                    </Box>{" "}
                                    {resource.Type}
                                  </Typography>
                                  {resource.Remediation?.Text && (
                                    <Typography
                                      sx={{ color: "#64748b", mb: 0, fontSize: "0.8125rem", fontWeight: 500 }}
                                    >
                                      <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>
                                        Recommendation:
                                      </Box>{" "}
                                      {resource.Remediation.Text}
                                      {resource.Remediation.Url && (
                                        <Typography
                                          component="button"
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openRemediationPreview(resource.Remediation.Url);
                                          }}
                                          sx={{
                                            display: "inline",
                                            ml: 1,
                                            p: 0,
                                            border: "none",
                                            background: "none",
                                            color: "#1976d2",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            font: "inherit",
                                          }}
                                        >
                                          Reference
                                        </Typography>
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {showRequestFix && (
                <Box sx={{ flexShrink: 0, borderTop: "1px solid #e2e8f0", px: 1.5, py: 1 }}>
                  <RemediationStatusLegend />
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </Drawer>

    <RemediationSnackbarProvider>
      <RequestFixForm
        open={fixFormOpen}
        onClose={() => setFixFormOpen(false)}
        preset={{
          checkId,
          checkTitle,
          accountNumber,
          resourceArns: Array.from(selectedArns),
        }}
        onSubmitted={() => {
          clearSelection();
          refetchActiveRequests();
        }}
      />
    </RemediationSnackbarProvider>
    <RemediationPreviewPanel
      url={remediationPreviewUrl}
      onClose={closeRemediationPreview}
    />
  </>
  );
};

export default CSPMResourcesDrawer;
